import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { getDateRange, formatDate, formatHours } from '../lib/utils'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Badge } from '../components/ui/badge'
import { useLanguage } from '../context/LanguageContext'
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer 
} from 'recharts'
import { Timer, Utensils, TrendingUp, Calendar, Clock } from 'lucide-react'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

export default function AyunoStatsPage() {
  const { t, locale } = useLanguage()
  const [dateRange, setDateRange] = useState(() => getDateRange(14))
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)

  const fetchStats = useCallback(async () => {
    setLoading(true)
    try {
      const response = await axios.get(`${API}/fasting/stats`, {
        params: {
          start_date: dateRange.start,
          end_date: dateRange.end
        }
      })
      setStats(response.data)
    } catch (error) {
      console.error('Error fetching stats:', error)
    } finally {
      setLoading(false)
    }
  }, [dateRange])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  const quickRanges = [
    { label: locale === 'es-MX' ? '7 días' : '7 days', days: 7 },
    { label: locale === 'es-MX' ? '14 días' : '14 days', days: 14 },
    { label: locale === 'es-MX' ? '30 días' : '30 days', days: 30 },
  ]

  const fastingChartData = stats?.daily_fast_hours?.map(d => ({
    date: formatDate(d.date).split(',')[0],
    hours: d.hours
  })) || []

  const eatingChartData = stats?.daily_eat_hours?.map(d => ({
    date: formatDate(d.date).split(',')[0],
    hours: d.hours
  })) || []

  const getMealQualityColor = (quality) => {
    switch (quality) {
      case 'Bien': return 'bg-emerald-500'
      case 'Regular': return 'bg-amber-500'
      case 'Mal': return 'bg-rose-500'
      default: return 'bg-zinc-500'
    }
  }

  return (
    <div className="p-4 md:p-8 pb-safe max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold mb-1">{t('nav.fastingProgress')}</h1>
        <p className="text-muted-foreground text-sm">
          {locale === 'es-MX' ? 'Analiza tus patrones de ayuno y alimentación' : 'Analyze your fasting and eating patterns'}
        </p>
      </div>

      {/* Date Range Selector */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {locale === 'es-MX' ? 'Rango de fechas' : 'Date range'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            {quickRanges.map((range) => (
              <Button
                key={range.days}
                variant="outline"
                size="sm"
                className="rounded-full"
                onClick={() => setDateRange(getDateRange(range.days))}
                data-testid={`fasting-range-${range.days}`}
              >
                {range.label}
              </Button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs font-medium">{locale === 'es-MX' ? 'Desde' : 'From'}</Label>
              <Input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="h-11 w-full"
                data-testid="fasting-date-start"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-medium">{locale === 'es-MX' ? 'Hasta' : 'To'}</Label>
              <Input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="h-11 w-full"
                data-testid="fasting-date-end"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="stat-card" data-testid="stat-total-fast">
            <div className="flex items-center gap-2 mb-2">
              <Timer className="w-4 h-4 text-violet-500" />
              <span className="text-xs text-muted-foreground">{locale === 'es-MX' ? 'Total ayuno' : 'Total fasting'}</span>
            </div>
            <p className="text-2xl font-bold">{stats.total_fast_hours}h</p>
          </div>
          
          <div className="stat-card" data-testid="stat-total-eat">
            <div className="flex items-center gap-2 mb-2">
              <Utensils className="w-4 h-4 text-emerald-500" />
              <span className="text-xs text-muted-foreground">{locale === 'es-MX' ? 'Total comida' : 'Total eating'}</span>
            </div>
            <p className="text-2xl font-bold">{stats.total_eat_hours}h</p>
          </div>
          
          <div className="stat-card" data-testid="stat-avg-fast">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-violet-500" />
              <span className="text-xs text-muted-foreground">{locale === 'es-MX' ? 'Prom. ayuno/día' : 'Avg. fast/day'}</span>
            </div>
            <p className="text-2xl font-bold">{stats.avg_fast_hours}h</p>
          </div>
          
          <div className="stat-card" data-testid="stat-avg-eat">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-emerald-500" />
              <span className="text-xs text-muted-foreground">{locale === 'es-MX' ? 'Prom. comida/día' : 'Avg. eat/day'}</span>
            </div>
            <p className="text-2xl font-bold">{stats.avg_eat_hours}h</p>
          </div>
        </div>
      )}

      {/* Fasting Hours Chart */}
      <Card className="mb-6" data-testid="fasting-chart">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Timer className="w-4 h-4 text-violet-500" />
            {locale === 'es-MX' ? 'Horas de ayuno por día' : 'Fasting hours by day'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={fastingChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 11 }}
                  stroke="hsl(var(--muted-foreground))"
                />
                <YAxis 
                  tick={{ fontSize: 11 }}
                  stroke="hsl(var(--muted-foreground))"
                  unit="h"
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                  formatter={(value) => [`${value}h`, locale === 'es-MX' ? 'Ayuno' : 'Fasting']}
                />
                <Bar dataKey="hours" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Eating Hours Chart */}
      <Card className="mb-6" data-testid="eating-chart">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Utensils className="w-4 h-4 text-emerald-500" />
            Horas de comida por día
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={eatingChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 11 }}
                  stroke="hsl(var(--muted-foreground))"
                />
                <YAxis 
                  tick={{ fontSize: 11 }}
                  stroke="hsl(var(--muted-foreground))"
                  unit="h"
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                  formatter={(value) => [`${value}h`, 'Comida']}
                />
                <Bar dataKey="hours" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Sessions List */}
      {stats?.sessions && stats.sessions.length > 0 && (
        <Card data-testid="sessions-list">
          <CardHeader>
            <CardTitle className="text-base">Historial de sesiones</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {stats.sessions
                .filter(s => s.meal_quality)
                .slice(0, 20)
                .map((session) => (
                <div 
                  key={session.id} 
                  className="flex items-center justify-between p-4 rounded-xl bg-secondary/50"
                  data-testid={`session-${session.id}`}
                >
                  <div>
                    <p className="font-medium">{formatDate(session.session_date)}</p>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                      {session.fast_hours && (
                        <span className="flex items-center gap-1">
                          <Timer className="w-3 h-3" />
                          {formatHours(session.fast_hours)}
                        </span>
                      )}
                      {session.eat_hours && (
                        <span className="flex items-center gap-1">
                          <Utensils className="w-3 h-3" />
                          {formatHours(session.eat_hours)}
                        </span>
                      )}
                    </div>
                  </div>
                  {session.meal_quality && (
                    <Badge className={`${getMealQualityColor(session.meal_quality)} text-white`}>
                      {session.meal_quality}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
